// @file Our library that track events to our data warehouse (on AWS) in order to process (through Snowflake)
import { trackEvent as trackRudderEvent } from '@@/bits/analytics'
import { screen, userAgent } from '@@/bits/browser'
import { captureException } from '@@/bits/error_tracker'
import { circularSafeStringify } from '@@/bits/json_stringify'
import { currentPath, currentUrl } from '@@/bits/location'
import * as Pepin from '@@/bits/pepin_types'
import { throttle } from 'lodash-es'
import type { User, Wall } from 'types'

let wall: Wall | undefined
let user: User
let environment = 'development'
let country = ''
let eventQueue: Pepin.EventData[] = []
let initialized = false
// Amazon API gateway that capture any data you send them and forward data to an S3 bucket.
// https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/ma73hwoovf/resources/uc2n3xooo3
const API_ENDPOINTS = {
  development: 'https://ma73hwoovf.execute-api.us-east-1.amazonaws.com/dev/streams/events_dev/record',
  staging: 'https://ma73hwoovf.execute-api.us-east-1.amazonaws.com/staging/streams/events_staging/record',
  production: 'https://ma73hwoovf.execute-api.us-east-1.amazonaws.com/prod/streams/events_prod/record',
}

function getUser(): Pepin.EventUser | undefined {
  if (!user) return undefined
  const eventUser: Pepin.EventUser = {
    id: String(user.id),
    tenantId: String(user.tenant_id),
    paying: !!user.paying,
    role: user.role || 'member',
    lang: user.lang,
    createdAt: user.created_at,
    wallsUsed: user.quota.walls_used,
    wallsLimit: user.quota.walls_limit,
    isRegistered: !!user.registered_at,
    country: country,
  }
  return eventUser
}

function getBeaconEndpoint(): string {
  return API_ENDPOINTS[environment]
}

function getContext(): Pepin.EventContext {
  return { userAgent, screen, url: currentUrl() }
}

function getRelativeTimestamp(): number {
  if (typeof window !== 'undefined' && window.performance) {
    return Math.round(window.performance.now() / 1000)
  }
  return -1
}

function getCurrentTimestamp(): number {
  return new Date().getTime()
}

function getSelectorStringFromHTMLElement(domElement: HTMLElement): string {
  let selectorString: string = domElement.tagName.toLowerCase()
  // For some reason, we are getting className.split is not a function
  if (domElement.className && domElement.className.split) {
    selectorString += `.${domElement.className.split(' ').join('.')}`
  }
  if (domElement.id) {
    selectorString += `#${domElement.id}`
  }
  return selectorString
}

function enqueueEvent(data: Pepin.EventData): void {
  eventQueue.push(data)
}

function sendBeaconFallback(url: string, payload: string, keepAlive = true): void {
  fetch(url, {
    body: payload,
    method: 'POST',
    credentials: 'omit',
    keepalive: keepAlive,
  }).catch((e) => {
    captureException(e)
  })
}

// Reference: https://xgwang.me/posts/you-may-not-know-beacon
function dequeueEvents(): void {
  if (!navigator.sendBeacon) return
  if (!initialized) {
    console.warn(
      '[Send event] Pepin is not initialized, tracked events may not appear in the correct environment or have missing user info',
    )
  }
  if (eventQueue.length > 0) {
    const stringifiedEventQueue: string[] = eventQueue.map((x: Pepin.EventData): string => circularSafeStringify(x))
    const payload: string = stringifiedEventQueue.join('\n')
    // Navigator has to be bound to ensure it does not fail in some browsers.
    const sendBeacon = navigator.sendBeacon.bind(navigator)
    const beaconEndpoint = getBeaconEndpoint()
    const isTestEnv = !beaconEndpoint
    if (!isTestEnv) {
      try {
        // The `navigator.sendBeacon` method uses `keepalive` internally
        // so the amount of data we can send is limited. In case the payload
        // exceeds the limit, the method will return `false`.
        // We fallback to `fetch` without the `keepalive` flag in these cases.
        const sendBeaconSucceeded = sendBeacon(beaconEndpoint, payload)
        if (!sendBeaconSucceeded) sendBeaconFallback(beaconEndpoint, payload, false)
      } catch {
        // If `navigator.sendBeacon` throws an exception, we fallback to `fetch`.
        sendBeaconFallback(beaconEndpoint, payload)
      }
    }
    eventQueue = []
  }
}

const throttledDequeueEvents = throttle(dequeueEvents, 1000, { trailing: true, leading: false })

function trackEvent(name: string, props: Pepin.EventProperties = {}): void {
  if (!initialized) {
    console.warn(
      '[Track event] Pepin is not initialized, tracked events may not appear in the correct environment or have missing user info',
    )
  }
  enqueueEvent({ name, props, user: getUser(), context: getContext(), timestamp: getCurrentTimestamp() })
  throttledDequeueEvents()
  // Jun 23 - Send clicks to Rudderstack as well for now. We will stop sending events to AWS API Gateway soon.
  trackRudderEvent('Click', 'Click', null, null, props)
}

function captureEveryClick(): void {
  document.addEventListener('click', (e): void => {
    const eventTarget = e.target as HTMLElement
    if (eventTarget) {
      const target = getSelectorStringFromHTMLElement(eventTarget)
      const coordinates = { x: e.pageX, y: e.pageY }
      const elapsedTime = getRelativeTimestamp()
      trackEvent('click', { target, coordinates, elapsedTime })
    }
  })
}

const WHITELISTED_CLICK_SELECTOR = 'button,a,[role=button],[data-pepin-trigger=click]'
function extractExtraDataProps(dataset: DOMStringMap): Record<string, any> {
  const extraData = {}
  Object.keys(dataset).forEach((dataKey: string): void => {
    if (dataKey.startsWith('pepin') && dataKey !== 'pepinId' && dataKey !== 'pepinTrigger') {
      const strippedDataKey = dataKey.slice(5)
      const camelizedDataKey = strippedDataKey.substr(0, 1).toLowerCase() + strippedDataKey.substr(1)
      extraData[camelizedDataKey] = dataset[dataKey]
    }
  })
  return extraData
}

function recordWhitelistedClick(e: MouseEvent): void {
  const eventTarget = e.target as HTMLElement
  const closestElementInSelectorList = eventTarget.closest(WHITELISTED_CLICK_SELECTOR) as HTMLElement
  if (closestElementInSelectorList) {
    const eventName = 'click'
    const target = closestElementInSelectorList.dataset.pepinId || closestElementInSelectorList.id
    const extraData = extractExtraDataProps(closestElementInSelectorList.dataset)
    if (target) {
      const coordinates = { x: e.pageX, y: e.pageY }
      const elapsedTime = getRelativeTimestamp()
      trackEvent(eventName, {
        target,
        coordinates,
        elapsedTime,
        ...extraData,
      })
    }
  }
}

function recordWallVisit(): void {
  if (!wall) return
  const path = currentPath()
  if (/\/wish\/\d+$/.test(path)) {
    const postId = Number(path.split('/').pop())
    trackEvent('post_url_expand_post', { owner_id: wall.builder.id, wall_id: wall.id, post_id: postId })
  }
  trackEvent('wall_visit', { owner_id: wall.builder.id, wall_id: wall.id })
}

function captureOnlyWhitelistedClicks(): void {
  document.addEventListener('click', recordWhitelistedClick)
}

function initEventTracking(config: Pepin.Config): void {
  user = config.user
  environment = config.environment
  wall = config.wall
  country = config.country
  window.addEventListener('beforeunload', dequeueEvents)
  initialized = true
}

function isPepinReady(): boolean {
  return initialized
}

export {
  captureEveryClick,
  captureOnlyWhitelistedClicks,
  initEventTracking,
  isPepinReady,
  recordWallVisit,
  recordWhitelistedClick,
  trackEvent,
}
